/**
 * Personal Information Form Section
 * Collects basic borrower information: name, email, phone, citizenship
 */

import {
  Button,
  FormTable,
  FormTableBody,
  PersonalInfoFields,
  UserAccountIcon,
  validatePersonalInfoField,
} from "@porchpass/ui";
import * as React from "react";
import { useApplicationFormStore } from "../hooks/useApplicationFormStore";
import { UpdateBorrowerContactDialog } from "./UpdateBorrowerContactDialog";

interface PersonalInformationFormProps {
  /** Whether this is for the co-borrower */
  isCoBorrower?: boolean;
}

export const PersonalInformationForm: React.FC<
  PersonalInformationFormProps
> = ({ isCoBorrower = false }) => {
  const store = useApplicationFormStore();
  const borrower = isCoBorrower
    ? store.data.coBorrower
    : store.data.primaryBorrower;
  const updateBorrower = isCoBorrower
    ? store.updateCoBorrower
    : store.updatePrimaryBorrower;

  const isContactLocked = !isCoBorrower && !!store.data.applicationId;

  const borrowerPrefix = isCoBorrower ? "coBorrower" : "primaryBorrower";
  const [showEditContactDialog, setShowEditContactDialog] =
    React.useState(false);

  if (!borrower) return null;
  // Duplicate warning messages
  const emailDuplicateWarning =
    store.duplicateWarnings[`${borrowerPrefix}.email`];
  const phoneDuplicateWarning =
    store.duplicateWarnings[`${borrowerPrefix}.phone`];
  const hasDuplicateWarning = emailDuplicateWarning || phoneDuplicateWarning;

  const clearIfValid = (field: string, value: string) => {
    if (
      store.fieldErrors[`${borrowerPrefix}.${field}`] &&
      !validatePersonalInfoField(field, value)
    ) {
      store.clearFieldError(`${borrowerPrefix}.${field}`);
    }
  };

  const handleBorrowerChange = (patch: Partial<typeof borrower>) => {
    for (const [field, value] of Object.entries(patch)) {
      if (field === "citizenship") {
        if (store.fieldErrors[`${borrowerPrefix}.citizenship`]) {
          store.clearFieldError(`${borrowerPrefix}.citizenship`);
        }
      } else if (typeof value === "string") {
        clearIfValid(field, value);
      }
    }
    updateBorrower(patch);
  };

  return (
    <>
      <FormTable
        title="Personal Information"
        headerIcon={
          <UserAccountIcon
            variant="bulk-rounded"
            size={16}
            className="text-[#6F41E6]"
          />
        }
      >
        <FormTableBody>
          {hasDuplicateWarning && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <div className="text-sm text-yellow-800">
                <strong>⚠️ Duplicate Found:</strong>{" "}
                {emailDuplicateWarning || phoneDuplicateWarning}
              </div>
            </div>
          )}
          <PersonalInfoFields
            value={borrower}
            onChange={handleBorrowerChange}
            errors={{
              firstName: store.fieldErrors[`${borrowerPrefix}.firstName`],
              lastName: store.fieldErrors[`${borrowerPrefix}.lastName`],
              middleName: store.fieldErrors[`${borrowerPrefix}.middleName`],
              suffix: store.fieldErrors[`${borrowerPrefix}.suffix`],
              email: store.fieldErrors[`${borrowerPrefix}.email`],
              phone: store.fieldErrors[`${borrowerPrefix}.phone`],
              citizenship: store.fieldErrors[`${borrowerPrefix}.citizenship`],
            }}
            disabled={{
              email: isContactLocked,
              phone: isContactLocked,
            }}
          />

          {isContactLocked && (
            <div className="pt-1 pb-1">
              <Button
                variant="ghost"
                size="s"
                onClick={() => setShowEditContactDialog(true)}
                className="text-sm text-[#3265ea] p-0 h-auto"
              >
                Edit email or phone
              </Button>
            </div>
          )}
        </FormTableBody>
      </FormTable>

      {isContactLocked && store.data.primaryBorrowerId && (
        <UpdateBorrowerContactDialog
          open={showEditContactDialog}
          onOpenChange={setShowEditContactDialog}
          currentEmail={borrower.email}
          currentPhone={borrower.phone}
          borrowerId={store.data.primaryBorrowerId}
          onSuccess={(email, phone) => {
            updateBorrower({ email, phone });
          }}
        />
      )}
    </>
  );
};

export default PersonalInformationForm;
